import client from '../../shared/api/client';
import {
  DepositAddressBatch,
  DepositEntry,
  DepositSigner,
  PaginatedResponse,
  PoolWallet,
  UserBalanceGroup,
  UserWalletGroup,
  WalletDetail,
  WalletOverview,
} from '../../shared/types';

interface LedgerBackendResponse<T> {
  success?: boolean;
  status?: string;
  data: T;
}

export interface WalletFilters {
  page?: number;
  per_page?: number;
  user_id?: string | number;
  currency?: string;
  address?: string;
  is_active?: '1' | '0';
}

export interface DepositFilters {
  page?: number;
  per_page?: number;
  user_id?: string | number;
  wallet_id?: number;
  currency?: string;
  reference_id?: string;
  date_from?: string;
  date_to?: string;
}

export const listWallets = async (
  filters: WalletFilters = {}
): Promise<PaginatedResponse<UserWalletGroup>> => {
  const params: Record<string, string | number | undefined> = {
    page: filters.page ?? 1,
    per_page: filters.per_page ?? 20,
  };
  if (filters.user_id) params.user_id = filters.user_id;
  if (filters.currency) params.currency = filters.currency;
  if (filters.address) params.address = filters.address;
  if (filters.is_active !== undefined) params.is_active = filters.is_active;

  const response = await client.get<LedgerBackendResponse<PaginatedResponse<UserWalletGroup>>>(
    '/ledger/wallets',
    { params }
  );
  return response.data.data;
};

export const setWalletActive = async (
  id: number,
  isActive: boolean
): Promise<WalletOverview> => {
  const response = await client.put<LedgerBackendResponse<WalletOverview>>(
    `/ledger/wallets/${id}/active`,
    { is_active: isActive }
  );
  return response.data.data;
};

export interface AssignWalletInput {
  user_id: number;
  wallet_id?: number;
  currency?: string;
  network?: string;
}

export const assignWalletToUser = async (
  input: AssignWalletInput
): Promise<WalletOverview> => {
  const response = await client.post<LedgerBackendResponse<WalletOverview>>(
    '/ledger/wallets/assign',
    input
  );
  return response.data.data;
};

export interface BalanceFilters {
  page?: number;
  per_page?: number;
  user_id?: string | number;
  currency?: string;
}

export const listBalances = async (
  filters: BalanceFilters = {}
): Promise<PaginatedResponse<UserBalanceGroup>> => {
  const params: Record<string, string | number | undefined> = {
    page: filters.page ?? 1,
    per_page: filters.per_page ?? 20,
  };
  if (filters.user_id) params.user_id = filters.user_id;
  if (filters.currency) params.currency = filters.currency;

  const response = await client.get<LedgerBackendResponse<PaginatedResponse<UserBalanceGroup>>>(
    '/ledger/balances',
    { params }
  );
  return response.data.data;
};

export const getWallet = async (id: number): Promise<WalletDetail> => {
  const response = await client.get<LedgerBackendResponse<WalletDetail>>(
    `/ledger/wallets/${id}`
  );
  return response.data.data;
};

export const listDeposits = async (
  filters: DepositFilters = {}
): Promise<PaginatedResponse<DepositEntry>> => {
  const params: Record<string, string | number | undefined> = {
    page: filters.page ?? 1,
    per_page: filters.per_page ?? 20,
  };
  if (filters.user_id) params.user_id = filters.user_id;
  if (filters.wallet_id) params.wallet_id = filters.wallet_id;
  if (filters.currency) params.currency = filters.currency;
  if (filters.reference_id) params.reference_id = filters.reference_id;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;

  const response = await client.get<LedgerBackendResponse<PaginatedResponse<DepositEntry>>>(
    '/ledger/deposits',
    { params }
  );
  return response.data.data;
};

export const getDeposit = async (id: number): Promise<DepositEntry> => {
  const response = await client.get<LedgerBackendResponse<DepositEntry>>(
    `/ledger/deposits/${id}`
  );
  return response.data.data;
};

export interface ScannedDeposit {
  transaction_id: number;
  tx_hash: string;
  amount: number;
  from: string;
  to: string;
  block_timestamp: number | null;
}

export interface SkippedDeposit {
  tx_hash: string;
  to: string;
  reason: string;
}

export interface ScanResult {
  scanned: number;
  credited: number;
  skipped: number;
  new_deposits: ScannedDeposit[];
  skipped_events: SkippedDeposit[];
  wallet: {
    id: number;
    user_id: number | null;
    currency: string;
    network: string | null;
    address: string;
  };
}

export const scanWalletDeposits = async (
  walletId: number,
  address?: string
): Promise<ScanResult> => {
  const response = await client.post<LedgerBackendResponse<ScanResult>>(
    `/ledger/wallets/${walletId}/scan`,
    address ? { address } : {}
  );
  return response.data.data;
};

export type PoolImportAddressStatus = 'new' | 'link' | 'exists' | 'error';

export interface PoolImportAddress {
  address: string;
  status: PoolImportAddressStatus;
  error: string | null;
  wallet_id: number | null;
}

export interface PoolImportPreview {
  kid: string;
  signer_label: string;
  jti: string;
  iat: string;
  currency: string;
  network: string;
  total: number;
  new: number;
  link: number;
  exists: number;
  invalid: number;
  errors: string[];
  addresses: PoolImportAddress[];
}

export interface PoolImportResult extends PoolImportPreview {
  batch_id: number;
  created_ids: number[];
  linked_ids: number[];
}

export interface PoolImportInput {
  jws: string;
  currency: string;
  network: string;
}

export const previewPoolImport = async (input: PoolImportInput): Promise<PoolImportPreview> => {
  const response = await client.post<LedgerBackendResponse<PoolImportPreview>>(
    '/ledger/wallets/pool/import/preview',
    { jws: input.jws.trim(), currency: input.currency, network: input.network }
  );
  return response.data.data;
};

export const importPoolBatch = async (input: PoolImportInput): Promise<PoolImportResult> => {
  const response = await client.post<LedgerBackendResponse<PoolImportResult>>(
    '/ledger/wallets/pool/import',
    { jws: input.jws.trim(), currency: input.currency, network: input.network }
  );
  return response.data.data;
};

export interface PoolBatchFilters {
  page?: number;
  per_page?: number;
  kid?: string;
  currency?: string;
}

export const listPoolBatches = async (
  filters: PoolBatchFilters = {}
): Promise<PaginatedResponse<DepositAddressBatch>> => {
  const params: Record<string, string | number | undefined> = {
    page: filters.page ?? 1,
    per_page: filters.per_page ?? 20,
  };
  if (filters.kid) params.kid = filters.kid;
  if (filters.currency) params.currency = filters.currency;

  const response = await client.get<LedgerBackendResponse<PaginatedResponse<DepositAddressBatch>>>(
    '/ledger/wallets/pool/batches',
    { params }
  );
  return response.data.data;
};

export const listPoolSigners = async (): Promise<DepositSigner[]> => {
  const response = await client.get<LedgerBackendResponse<DepositSigner[]>>(
    '/ledger/wallets/pool/signers'
  );
  return response.data.data;
};

export interface RevokeSignerResult {
  kid: string;
  already_revoked: boolean;
  revoked_at: string | null;
  batches: number;
  wallets_deactivated: number;
}

export const revokePoolSigner = async (kid: string, reason?: string): Promise<RevokeSignerResult> => {
  const response = await client.post<LedgerBackendResponse<RevokeSignerResult>>(
    `/ledger/wallets/pool/signers/${encodeURIComponent(kid)}/revoke`,
    reason ? { reason } : {}
  );
  return response.data.data;
};

export interface PoolWalletFilters {
  page?: number;
  per_page?: number;
  currency?: string;
  address?: string;
}

export const listPoolWallets = async (
  filters: PoolWalletFilters = {}
): Promise<PaginatedResponse<PoolWallet>> => {
  const params: Record<string, string | number | undefined> = {
    page: filters.page ?? 1,
    per_page: filters.per_page ?? 20,
  };
  if (filters.currency) params.currency = filters.currency;
  if (filters.address) params.address = filters.address;

  const response = await client.get<LedgerBackendResponse<PaginatedResponse<PoolWallet>>>(
    '/ledger/wallets/pool',
    { params }
  );
  return response.data.data;
};

export const deletePoolWallet = async (id: number): Promise<void> => {
  await client.delete(`/ledger/wallets/pool/${id}`);
};
